import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote, UtensilsCrossed, Truck, X, Check, Calculator, Receipt, Clock, AlertCircle, Play, ShoppingBag, Percent, Lock, Search, Printer, ChefHat, Edit2, RefreshCw, Edit, ChevronUp, History, Eye, Calendar, Undo, ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import API_URL from "@/utils/api";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { offlineDB } from "@/utils/offlineDB";
import { OnlineStatusIndicator } from "@/components/OnlineStatusIndicator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import printService from "@/services/PrintService";
import PrinterSettings from "@/components/PrinterSettings";
import PrintQueueWorker from "@/components/admin/PrintQueueWorker";
import { MapPin, Boxes } from "lucide-react";
import DemoBanner from "@/shell/DemoBanner";


const API = API_URL;

// Debug: Log environment info
if (typeof window !== 'undefined') {
  console.log('[AdminPOS] API URL:', API);
}

export default function AdminPOS() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Offline mode support
  const isOnline = useOnlineStatus();

  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // View mode: "new" for new orders, "pending" for pending payments
  const [viewMode, setViewMode] = useState("new");

  // Pending orders (orders with payment_status = pending)
  const [pendingOrders, setPendingOrders] = useState([]);

  // Order settings
  const [orderType, setOrderType] = useState("local"); // local, delivery, or pickup
  const [tableNumber, setTableNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [orderNotes, setOrderNotes] = useState("");

  // Payment dialog
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cashReceived, setCashReceived] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPayingCard, setIsPayingCard] = useState(false);
  const lastOrderStatusesRef = useRef({}); // {order_id: status}
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [isFrequentCustomer, setIsFrequentCustomer] = useState(false);
  const [customerDataConfirmed, setCustomerDataConfirmed] = useState(false); // New: Collapse inputs

  // Discount with admin auth
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [isVerifyingDiscount, setIsVerifyingDiscount] = useState(false);

  const [paymentNote, setPaymentNote] = useState(""); // Change calculation state
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [appliedCouponInfo, setAppliedCouponInfo] = useState(null);

  // Selected pending order for payment
  const [selectedPendingOrder, setSelectedPendingOrder] = useState(null);
  const [showPendingPayment, setShowPendingPayment] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState("cash");

  // Filter for pending orders
  const [pendingFilter, setPendingFilter] = useState("all"); // all, local, delivery, pickup
  const [searchQuery, setSearchQuery] = useState(""); // Search in pending orders

  // Active shift state
  const [activeShift, setActiveShift] = useState(null);
  const [checkingShift, setCheckingShift] = useState(true);

  // Edit Mode state
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [originalOrder, setOriginalOrder] = useState(null);

  // Branch support
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  // Printer status state
  const [availableTables, setAvailableTables] = useState([]);
  const [printerStatus, setPrinterStatus] = useState("checking"); // "checking", "connected", "disconnected"
  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const [showImages, setShowImages] = useState(true); // User request: Optional images for POS

  // Delivery Hub & Integration State
  const [hubStatus, setHubStatus] = useState("connected"); // "connected", "warning", "offline"
  const [ridersAvailable, setRidersAvailable] = useState(sessionStorage.getItem("demo_mode") === "true" ? 12 : 0);

  // Print Queue State
  const [printQueueStatus, setPrintQueueStatus] = useState({ count: 0, processing: false });

  // Check printer connection (ZT-Bridge)
  useEffect(() => {
    const checkBridge = async () => {
      try {
        const res = await fetch('http://localhost:8001', { method: 'OPTIONS' });
        setPrinterStatus(res.ok ? "connected" : "disconnected");
      } catch (e) {
        setPrinterStatus("disconnected");
      }
    };
    checkBridge();
    const interval = setInterval(checkBridge, 30000);
    return () => clearInterval(interval);
  }, []);


  // History state
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilters, setHistoryFilters] = useState({
    preset: 'last7',
    status: 'all',
    type: 'all',
    table: '',
    q: '',
    offset: 0,
    limit: 10
  });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedHistoryOrder, setSelectedHistoryOrder] = useState(null);
  const [showHistoryDetail, setShowHistoryDetail] = useState(false);



  const fetchActiveShift = useCallback(async () => {
    try {
      const branchParam = selectedBranch && selectedBranch !== "all" ? `?branch_id=${selectedBranch}` : "";
      const response = await axios.get(`${API}/admin/cashier-shifts/active${branchParam}`, { withCredentials: true });
      if (response.data.has_active_shift) {
        setActiveShift(response.data.shift);
        // Sync branch if not set
        if (selectedBranch === "all" && response.data.shift.branch_id) {
          setSelectedBranch(response.data.shift.branch_id);
        }
      } else {
        setActiveShift(null);
      }
    } catch (error) {
      console.error("Error fetching active shift:", error);
      setActiveShift(null);
    } finally {
      setCheckingShift(false);
    }
  }, [selectedBranch]);

  const fetchHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const { preset, status, type, table, q, offset, limit } = historyFilters;
      const params = new URLSearchParams({
        preset,
        status,
        order_type: type,
        table_number: table,
        q,
        limit,
        offset,
        branch_id: selectedBranch
      });
      const res = await axios.get(`${API}/admin/orders/history?${params.toString()}`, { withCredentials: true });
      setHistoryOrders(res.data.orders);
      setHistoryTotal(res.data.total);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Error al cargar historial");
    } finally {
      setIsLoadingHistory(false);
    }
  }, [historyFilters, selectedBranch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === 'history') {
      setViewMode('history');
    }
  }, [location.search]);

  useEffect(() => {
    if (viewMode === "history") {
      fetchHistory();
    }
  }, [viewMode, fetchHistory]);

  const handleViewHistoryDetail = async (order) => {
    try {
      // Fetch full detail including events
      const res = await axios.get(`${API}/admin/orders/${order.order_id}`, { withCredentials: true });
      setSelectedHistoryOrder(res.data);
      setShowHistoryDetail(true);
    } catch (error) {
      console.error("Error fetching order detail:", error);
      toast.error("Error al cargar detalles del pedido");
    }
  };

  const handleReprint = async (target) => {
    if (!selectedHistoryOrder) return;
    try {
      await axios.post(`${API}/admin/orders/${selectedHistoryOrder.order_id}/reprint`, { target }, { withCredentials: true });
      toast.success(`Reimpresión de ${target === 'customer' ? 'ticket' : 'comanda'} encolada`);
      // Update local state to show pending
      setSelectedHistoryOrder(prev => ({
        ...prev,
        [target === 'customer' ? 'customer_print_status' : 'kitchen_print_status']: 'pending'
      }));
    } catch (error) {
      console.error("Error en reimpresión:", error);
      toast.error("Error al solicitar reimpresión");
    }
  };

  const fetchBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/branches`, { withCredentials: true });
      setBranches(res.data);
      // Auto-select user branch
      if (user?.assigned_branches?.length > 0 && selectedBranch === "all") {
        setSelectedBranch(user.assigned_branches[0]);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, [user, selectedBranch]);

  const playNotificationSound = useCallback(() => {
    const audio = new Audio("/sounds/ubereats_ping.mp3");
    audio.volume = 0.5;
    audio.play().catch(e => console.warn("Audio play failed:", e));
  }, []);

  const playRiderSound = useCallback(() => {
    const audio = new Audio("/sounds/conexo_alert.mp3");
    audio.volume = 0.6;
    audio.play().catch(e => console.warn("Audio play failed:", e));
  }, []);

  const simulateOrderReady = (orderId, table, name) => {
    setTimeout(() => {
      playNotificationSound();
      toast("🔔 ¡PEDIDO LISTO EN COCINA!", {
        description: `Mesa ${table || 'Caja'} - ${name || 'Cliente'} está listo para servir.`,
        duration: 10000,
        icon: "👨‍🍳",
        style: {
          background: 'var(--primary, #1a40d0)',
          color: '#fff',
          fontWeight: "bold"
        }
      });
    }, 45000); // 45 seconds for demo realism
  };

  // Fetch menu data, pending orders, AND available tables
  const fetchData = useCallback(async () => {
    try {
      const branchParam = selectedBranch && selectedBranch !== "all" ? `?branch_id=${selectedBranch}` : "";
      const [catRes, menuRes, ordersRes, tablesRes] = await Promise.all([
        axios.get(`${API}/categories`, { withCredentials: true }),
        axios.get(`${API}/menu`, { withCredentials: true }),
        axios.get(`${API}/admin/orders${branchParam}`, { withCredentials: true }),
        axios.get(`${API}/tables`, { withCredentials: true })
      ]);
      setCategories(catRes.data.filter(c => c.active));
      setMenuItems(menuRes.data.filter(m => m.active));

      // Store available tables for auto-assignment
      // We attach it to the window object or a ref if we don't want to add state, 
      // but adding a state variable is cleaner. 
      // For now, let's just stick to the simplest solution:
      // We will filter on the fly or improved: add tables to state.
      setAvailableTables(tablesRes.data || []);

      // Filter orders that are pending payment (delivered but not paid, or ready for payment)
      const allOrders = ordersRes.data || [];

      // DETECT STATUS CHANGES (READY NOTIFICATION)
      allOrders.forEach(order => {
        const oldStatus = lastOrderStatusesRef.current[order.order_id];
        if (oldStatus && oldStatus !== "ready" && order.status === "ready") {
          // Notify!
          playNotificationSound();
          toast(`🔔 ¡Mesa ${order.table_number || 'Caja'} Lista!`, {
            description: "El pedido ha sido marcado como listo en cocina.",
            duration: 8000,
            icon: "🍽️"
          });
        }
        lastOrderStatusesRef.current[order.order_id] = order.status;
      });

      const pending = allOrders.filter(o =>
        o.payment_status === "pending" &&
        !["cancelled"].includes(o.status)
      );
      setPendingOrders(pending);

      if (catRes.data.length > 0) {
        setCategories(cats => cats);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    fetchData();
    fetchActiveShift();

    // Check printer connection
    const checkPrinter = async () => {
      const isConnected = await printService.testLocalConnection();
      setPrinterStatus(isConnected ? "connected" : "disconnected");
    };
    checkPrinter();

    // Auto-refresh pending orders AND printer status
    const interval = setInterval(() => {
      fetchData();
      fetchActiveShift();
      checkPrinter();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData, fetchActiveShift, selectedBranch]);

  const simulateIntegrationOrder = async (platform) => {
    const platforms = {
      uber: { name: "Uber Eats", color: "#06C167", source: "platform_ubereats" },
      glovo: { name: "Glovo", color: "#FFC244", source: "platform_glovo" },
      justeat: { name: "Just Eat", color: "#EA000B", source: "platform_justeat" }
    };
    const config = platforms[platform];

    const mockOrder = {
      branch_id: selectedBranch !== "all" ? selectedBranch : null,
      source: config.source,
      order_type: "delivery",
      customer_name: `Demo ${config.name}`,
      customer_phone: "600000000",
      total: 35.50,
      payment_status: "paid",
      payment_method: "card",
      items: [
        { name: "Súper Taco XL", quantity: 2, price: 12.00 },
        { name: "Nachos Integración", quantity: 1, price: 11.50 }
      ],
      address: {
        street: "Calle Demo de Prueba 123",
        city: "Almería",
        postal_code: "04001"
      }
    };

    try {
      toast.info(`Simulando pedido de ${config.name}...`);
      const response = await axios.post(`${API}/orders/pos`, mockOrder, { withCredentials: true });
      playRiderSound();
      toast(`🔥 ¡NUEVO PEDIDO DE ${config.name.toUpperCase()}!`, {
        description: "Pedido externo guardado en base de datos. ¡Búscalo en Reporte de Caja!",
        icon: "🔌",
        duration: 8000,
        style: {
          background: config.color,
          color: platform === 'glovo' ? '#000' : '#fff',
          fontWeight: 'bold'
        }
      });
      fetchData(); // Refresh list to show the new order
    } catch (err) {
      console.error("Error simulating order:", err);
      toast.error("Error al simular pedido externo");
    }
  };

  const handleDispatchToHub = async (order) => {
    setIsProcessing(true);
    toast.info("Llamando a Delivery Hub...", { icon: "🚀" });

    try {
      // Real backend update
      await axios.patch(`${API}/admin/orders/${order.order_id}/dispatch-hub`, {}, { withCredentials: true });

      // Play specific notification sound for Hub action
      playRiderSound();

      toast.success("¡Rider asignado en el Hub!", {
        description: "El repartidor más cercano ha aceptado el servicio.",
        icon: "🛵",
        duration: 5000
      });

      // Remove from pending list if successful
      setPendingOrders(prev => prev.filter(o => o.order_id !== order.order_id));
      setSelectedPendingOrder(null);
    } catch (error) {
      console.error("Error dispatching to hub:", error);
      toast.error("Error al despachar al Hub: " + (error.response?.data?.detail || "Error de conexión"));
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const table = params.get('table');
    const view = params.get('view');
    const search = params.get('search');
    const editOrder = params.get('edit_order');

    if (table) {
      setTableNumber(table);
      setOrderType("local");
      setViewMode("new");
    }
    if (view) {
      setViewMode(view);
    }
    if (search) {
      setSearchQuery(search);
    }
    if (editOrder) {
      loadOrderToEdit(editOrder);
    }
  }, [location.search]);

  const loadOrderToEdit = async (orderId) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      if (res.data) {
        setEditingOrderId(orderId);
        setOriginalOrder(res.data);
        setOrderType(res.data.order_type);
        setTableNumber(res.data.table_number || "");
        setCustomerName(res.data.customer_name || "");
        setCustomerPhone(res.data.customer_phone || "");
        setOrderNotes(res.data.notes || "");

        // Cargar productos al carrito
        // Fix: If order is already in kitchen/printed, assume current items are printed if specific field is missing
        const isOrderPrinted = res.data.kitchen_printed || ['preparing', 'ready', 'delivered', 'served'].includes(res.data.status);

        setCart(res.data.items.map(item => ({
          ...item,
          printed_quantity: item.printed_quantity !== undefined
            ? item.printed_quantity
            : (isOrderPrinted ? item.quantity : 0)
        })));

        setViewMode("new");
        toast.info(`Editando pedido #${orderId.slice(-6)}`, { icon: "📝" });
      }
    } catch (error) {
      console.error("Error loading order to edit:", error);
      toast.error("No se pudo cargar el pedido para editar");
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEditMode = () => {
    setEditingOrderId(null);
    setOriginalOrder(null);
    clearCart();
    setTableNumber("");
    setCustomerName("");
    setCustomerPhone("");
    setOrderNotes("");
    setViewMode("pending"); // Volver a vista pendientes para cobrar
    toast.info("Modo edición cancelado");
  };

  // CRM: Search customer by phone
  useEffect(() => {
    const searchCustomer = async () => {
      if (customerPhone && customerPhone.length >= 9) {
        setIsSearchingCustomer(true);
        try {
          const res = await axios.get(`${API}/admin/customers/search/${customerPhone}`, { withCredentials: true });
          if (res.data) {
            // Fill if empty or if frequent customer detected
            if (!customerName) setCustomerName(res.data.name);
            if (!deliveryAddress) setDeliveryAddress(res.data.address);
            setIsFrequentCustomer(true);
            toast.success(`Cliente reconocido: ${res.data.name}`, { icon: "🔥", duration: 2000 });
          }
        } catch (error) {
          setIsFrequentCustomer(false);
          // 404 is normal for new customers
        } finally {
          setIsSearchingCustomer(false);
        }
      } else {
        setIsFrequentCustomer(false);
      }
    };

    const timer = setTimeout(() => {
      searchCustomer();
    }, 700); // Debounce

    return () => clearTimeout(timer);
  }, [customerPhone]);

  const handleManualCustomerSearch = async () => {
    if (!customerPhone || customerPhone.length < 9) {
      toast.info("Introduce un teléfono válido para buscar");
      return;
    }
    setIsSearchingCustomer(true);
    try {
      const res = await axios.get(`${API}/admin/customers/search/${customerPhone}`, { withCredentials: true });
      if (res.data) {
        setCustomerName(res.data.name);
        setDeliveryAddress(res.data.address);
        setIsFrequentCustomer(true);
        toast.success(`Cliente: ${res.data.name}`);
      } else {
        setIsFrequentCustomer(false);
        toast.info("No hay registros para este teléfono");
      }
    } catch (error) {
      setIsFrequentCustomer(false);
      toast.info("Nuevo cliente / Sin registro");
    } finally {
      setIsSearchingCustomer(false);
    }
  };


  // Filter items by category
  const filteredItems = selectedCategory
    ? menuItems.filter(item => item.category_id === selectedCategory)
    : menuItems;

  // Helper for platform orders
  const isPlatformOrder = (o) => {
    const s = (o.source || "").toLowerCase();
    return s.startsWith("platform_") || ["uber", "glovo", "justeat", "ubereats"].includes(s) || !!o.platform;
  };

  // Filtered pending orders with search
  let filteredPendingOrders = pendingFilter === "all"
    ? pendingOrders
    : pendingFilter === "integrations"
      ? pendingOrders.filter(o => isPlatformOrder(o))
      : pendingFilter === "delivery"
        ? pendingOrders.filter(o => o.order_type === "delivery" && !isPlatformOrder(o))
        : pendingOrders.filter(o => o.order_type === pendingFilter);

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredPendingOrders = filteredPendingOrders.filter(order => {
      const orderId = order.order_id.toLowerCase();
      const customerName = (order.customer_name || "").toLowerCase();
      const tableNumber = (order.table_number || "").toString().toLowerCase();
      const phone = (order.customer_phone || "").toLowerCase();
      const userName = (order.user_name || "").toLowerCase();

      return orderId.includes(query) ||
        customerName.includes(query) ||
        tableNumber.includes(query) ||
        phone.includes(query) ||
        userName.includes(query);
    });
  }

  // Base list for counting (apply search first)
  const baseForCounts = searchQuery.trim() ? pendingOrders.filter(order => {
    const query = searchQuery.toLowerCase();
    return order.order_id.toLowerCase().includes(query) ||
      (order.customer_name || "").toLowerCase().includes(query) ||
      (order.table_number || "").toString().toLowerCase().includes(query) ||
      (order.customer_phone || "").toLowerCase().includes(query) ||
      (order.user_name || "").toLowerCase().includes(query);
  }) : pendingOrders;

  const localCount = baseForCounts.filter(o => o.order_type === "local").length;
  const deliveryCount = baseForCounts.filter(o => o.order_type === "delivery" && !isPlatformOrder(o)).length;
  const pickupCount = baseForCounts.filter(o => o.order_type === "pickup").length;
  const integrationsCount = baseForCounts.filter(o => isPlatformOrder(o)).length;

  // Cart functions
  const addToCart = (item) => {
    console.log('[AdminPOS] Adding to cart:', { item_id: item.item_id, name: item.name, price: item.price });
    setCart(prev => {
      const existing = prev.find(i => i.item_id === item.item_id);
      if (existing) {
        const updated = prev.map(i =>
          i.item_id === item.item_id
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
        console.log('[AdminPOS] Cart updated (increment):', updated);
        return updated;
      }
      const newCart = [...prev, { ...item, quantity: 1, extras: [], notes: "" }];
      console.log('[AdminPOS] Cart updated (new item):', newCart);
      return newCart;
    });
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(i => {
      // Prevent removing items that are already printed in kitchen (Edit Mode)
      if (editingOrderId && i.printed_quantity > 0 && i.item_id === itemId) {
        toast.error("No se puede eliminar un artículo ya enviado a cocina");
        return true; // Keep it
      }
      return i.item_id !== itemId;
    }));
  };

  const updateQuantity = (itemId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.item_id === itemId) {
        const newQty = item.quantity + delta;
        // Edit Mode Protection: Cannot reduce below printed_quantity
        if (editingOrderId && item.printed_quantity > 0 && newQty < item.printed_quantity) {
          toast.error("No se puede reducir la cantidad de lo ya enviado a cocina");
          return item;
        }
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const clearCart = () => {
    setCart([]);
    setTableNumber("");
    setCustomerName("");
    setCustomerPhone("");
    setDeliveryAddress("");
    setOrderNotes("");
    setPaymentNote("");
    setAppliedDiscount(null);
    setAppliedCouponInfo(null);
    setCouponCode("");
    setCustomerDataConfirmed(false);
  };

  // Calculate totals using useMemo for reactivity
  const subtotal = useMemo(() => {
    const result = cart.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQty = parseInt(item.quantity) || 0;
      const extrasTotal = (item.extras || []).reduce((eSum, extra) =>
        eSum + (parseFloat(extra.price) || 0), 0
      );
      return sum + ((itemPrice + extrasTotal) * itemQty);
    }, 0);
    console.log('[AdminPOS] Calculated subtotal:', result, 'from cart:', cart.length, 'items');
    return result;
  }, [cart]);

  const deliveryFee = orderType === "delivery" ? 2.50 : 0;

  const discountAmount = useMemo(() => {
    let disc = 0;
    if (appliedDiscount) {
      if (appliedDiscount.percent === 100) disc = subtotal + deliveryFee;
      else disc = (subtotal * appliedDiscount.percent) / 100;
    }
    if (appliedCouponInfo) {
      if (appliedCouponInfo.discount_type === 'percent') {
        disc += (subtotal * appliedCouponInfo.discount_value) / 100;
      } else {
        disc += appliedCouponInfo.discount_value;
      }
    }
    return disc;
  }, [subtotal, appliedDiscount, appliedCouponInfo, deliveryFee]);

  const total = Math.max(0, subtotal + deliveryFee - discountAmount);

  // Verify admin password for discount
  const handleVerifyDiscount = async () => {
    if (!discountPercent || parseFloat(discountPercent) <= 0) {
      toast.error("Introduce un porcentaje de descuento válido");
      return;
    }
    if (!adminPassword) {
      toast.error("Introduce la contraseña del administrador");
      return;
    }

    setIsVerifyingDiscount(true);
    try {
      const response = await axios.post(`${API}/admin/verify-admin-discount`, {
        admin_password: adminPassword,
        discount_percent: parseFloat(discountPercent),
        reason: discountReason
      }, { withCredentials: true });

      if (response.data.authorized) {
        setAppliedDiscount({
          percent: parseFloat(discountPercent),
          reason: discountReason,
          authorized_by: response.data.authorized_by
        });
        toast.success(`Descuento del ${discountPercent}% autorizado por ${response.data.authorized_by}`);
        setShowDiscountDialog(false);
        setAdminPassword("");
        setDiscountPercent("");
        setDiscountReason("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al verificar descuento");
    } finally {
      setIsVerifyingDiscount(false);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode) return;
    setIsValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/admin/coupons/validate`, {
        code: couponCode,
        phone: customerPhone
      }, { withCredentials: true });

      setAppliedCouponInfo(response.data);
      toast.success(`Cupón aplicado: ${response.data.discount_type === 'percent' ? response.data.discount_value + '%' : '€' + response.data.discount_value}`);
      setShowCouponDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cupón inválido o expirado");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  // Process order without payment (for phone orders - pickup/delivery)
  const handleConfirmOrderWithoutPayment = async () => {

    // Validate based on order type
    // Auto-fill logic for speed (User Request)
    let finalTableNumber = tableNumber;
    let finalCustomerName = customerName;

    if (orderType === "local") {
      if (!finalTableNumber || !finalTableNumber.trim()) {
        // Find first available table
        const firstAvailable = availableTables.find(t => t.status === "available");

        if (firstAvailable) {
          finalTableNumber = firstAvailable.number;
          toast.success(`Mesa asignada automáticamente: ${finalTableNumber}`);
        } else {
          // Fallback if no tables available
          // Try to find ANY table
          const anyTable = availableTables[0];
          if (anyTable) {
            finalTableNumber = anyTable.number;
            toast.warning(`No hay mesas libres. Asignando mesa ${finalTableNumber}`);
          } else {
            finalTableNumber = "BARRA"; // Fallback to BARRA if absolutely no tables
            toast.info("Asignado a BARRA (Sin mesas configuradas)");
          }
        }
      }
    } else {
      // Pickup or Delivery auto-name
      if (!finalCustomerName || !finalCustomerName.trim()) {
        finalCustomerName = `Cliente ${Math.floor(Math.random() * 1000)}`;
        toast.info(`Nombre autogenerado: ${finalCustomerName}`);
      }
    }

    if (orderType === "delivery" && !deliveryAddress.trim()) {
      toast.error("La dirección es obligatoria para el reparto");
      return;
    }

    setIsProcessing(true);
    try {
      const orderData = {
        branch_id: selectedBranch !== "all" ? selectedBranch : null,
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          extras: item.extras || [],
          notes: item.notes || ""
        })),
        order_type: orderType,
        table_number: orderType === "local" ? finalTableNumber : null,
        address: orderType === "delivery" ? {
          street: deliveryAddress,
          city: "Almería",
          postal_code: "04001"
        } : null,
        customer_name: finalCustomerName || (orderType === "local" ? `Mesa ${finalTableNumber}` : "Cliente Teléfono"),
        customer_phone: customerPhone || "",
        payment_method: "pending", // Will be set when customer pays
        // Add payment note to order notes if exists (for delivery change)
        notes: (orderNotes + (paymentNote && parseFloat(paymentNote) > total ? ` [PAGA CON: ${paymentNote}€ - CAMBIO: ${(parseFloat(paymentNote) - total).toFixed(2)}€]` : "")).trim(),
        subtotal: subtotal,
        delivery_fee: deliveryFee,
        total: total,
        payment_status: "pending", // Customer will pay later
        coupon_code: appliedCouponInfo?.code || null,
        source: "pos"
      };

      if (editingOrderId) {
        // ACTUALIZAR PEDIDO EXISTENTE (Sin pago)
        await axios.put(`${API}/orders/${editingOrderId}`, {
          items: orderData.items,
          order_type: orderData.order_type,
          table_number: orderData.table_number,
          customer_name: orderData.customer_name,
          customer_phone: orderData.customer_phone,
          notes: orderData.notes,
          total: orderData.total
        }, { withCredentials: true });

        toast.success("Pedido actualizado y enviado a cocina", {
          icon: "🍳",
          duration: 5000
        });
        setEditingOrderId(null);
        setOriginalOrder(null);
        clearCart();
        setTableNumber("");
        setCustomerName("");
        fetchData();
      } else if (isOnline) {
        // Modo Online tradicional
        try {
          const response = await axios.post(`${API}/orders/pos`, orderData, {
            withCredentials: true
          });

          const fullOrder = response.data;

          toast.success(`Pedido #${fullOrder.order_id.slice(-6)} enviado a cocina - Pendiente de cobro`, {
            duration: 5000,
            icon: "🍳"
          });

          // IMPRESIÓN SILENCIOSA VÍA FRONTEND - Desactivada (El PrintQueueWorker lo hará)
          /*
          console.log("[AdminPOS] Imprimiendo COMANDA...");
          printService.printOrder(fullOrder, "Conexo POS", null, "kitchen");
          */

          // CRM: Save/Update Customer automatically for deferred orders too
          if (customerPhone && customerPhone.length >= 9) {
            try {
              await axios.post(`${API}/admin/customers`, {
                phone: customerPhone,
                name: finalCustomerName,
                address: deliveryAddress
              }, { withCredentials: true });
            } catch (crmError) {
              console.error("Error saving customer to CRM:", crmError);
            }
          }

          clearCart();
          fetchData(); // Refresh pending orders
        } catch (apiError) {
          // Error online, guardar offline
          console.error("API Error, guardando offline:", apiError);
          await offlineDB.savePendingOrder(orderData);
          toast.warning("⚠️ Sin conexión - Pedido guardado offline");
          clearCart();
        }
      } else {
        // Modo Offline
        await offlineDB.savePendingOrder(orderData);
        toast.warning("📴 MODO OFFLINE - Pedido guardado (se sincronizará automáticamente)");
        clearCart();
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Error al crear el pedido");
    } finally {
      setIsProcessing(false);
    }
  };

  // Print tickets function - sends to thermal printer
  const printTickets = async (orderId) => {
    try {
      // Intentar usar el servicio centralizado que ya maneja localhost:8001 automáticamente
      const orderRes = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      const order = orderRes.data;

      console.log("[AdminPOS] Intentando imprimir mediante PrintService...");
      await printService.printOrder(order, "Conexo POS");

      // Enviamos a cocina si es un pedido nuevo (aunque el service ya suele hacerlo si es kitchen)
      // Pero aquí estamos en POS, imprimimos el ticket de cliente principalmente.
      toast.success("Ticket enviado a impresora");
    } catch (error) {
      console.error("Error printing tickets:", error);
      toast.error("Error al imprimir tickets. Asegúrate de que el servicio de impresión esté activo.");
    }
  };

  // Print to thermal printer (Fallback with hidden iframe)
  const printToThermalPrinter = (ticketText, ticketType) => {
    try {
      // Create a hidden iframe for automatic printing
      const iframeId = 'print-iframe-' + Date.now();
      let printFrame = document.getElementById(iframeId);

      if (!printFrame) {
        printFrame = document.createElement('iframe');
        printFrame.id = iframeId;
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = 'none';
        document.body.appendChild(printFrame);
      }

      const printDoc = printFrame.contentWindow.document;
      printDoc.open();
      printDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Ticket ${ticketType}</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.3;
              margin: 0;
              padding: 5mm;
              white-space: pre-wrap;
            }
          </style>
        </head>
        <body>${ticketText}</body>
        </html>
      `);
      printDoc.close();

      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();

        // Remove after use
        setTimeout(() => {
          if (document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
        }, 5000);
      }, 500);

    } catch (error) {
      console.error(`Error printing ${ticketType} ticket:`, error);
      toast.error(`Error al imprimir ticket de ${ticketType}`);
    }
  };

  // Process order
  const handleProcessOrder = async () => {
    if (cart.length === 0) {
      toast.error("El carrito está vacío");
      return;
    }

    // Check if shift is active
    if (!activeShift) {
      toast.error("Debes abrir un turno antes de vender. Ve a Reporte de Caja para abrir turno.");
      setShowPayment(false);
      return;
    }


    // Validate based on order type
    // Auto-fill logic for speed (User Request)
    let finalTableNumber = tableNumber;
    let finalCustomerName = customerName;

    if (orderType === "local") {
      if (!finalTableNumber || !finalTableNumber.trim()) {
        const isDemo = sessionStorage.getItem("demo_mode") === "true";

        if (isDemo && availableTables.length > 0) {
          // Option C: Assign the first free table
          const busyTableNumbers = pendingOrders
            .filter(o => o.order_type === "local" && o.table_number)
            .map(o => o.table_number.toString());

          const freeTable = availableTables.find(t => !busyTableNumbers.includes(t.number.toString()));

          if (freeTable) {
            finalTableNumber = freeTable.number.toString();
            toast.info(`Mesa libre encontrada y asignada: ${finalTableNumber}`);
          } else {
            // Fallback to random if all tables are busy
            const randomTable = Math.floor(Math.random() * 99) + 1;
            finalTableNumber = randomTable.toString();
            toast.info(`Mesas llenas. Usando mesa temporal: ${finalTableNumber}`);
          }
        } else {
          // Standard random fill for speed
          const randomTable = Math.floor(Math.random() * 99) + 1;
          finalTableNumber = randomTable.toString();
          toast.info(`Mesa autogenerada: ${finalTableNumber}`);
        }
      }
    } else {
      // Pickup or Delivery auto-name
      if (!finalCustomerName || !finalCustomerName.trim()) {
        finalCustomerName = `Cliente ${Math.floor(Math.random() * 1000)}`;
        toast.info(`Nombre autogenerado: ${finalCustomerName}`);
      }
    }

    if (orderType === "delivery" && (!deliveryAddress || !deliveryAddress.trim())) {
      toast.error("La dirección es obligatoria para el reparto");
      setIsProcessing(false);
      return;
    }

    setIsProcessing(true);

    // REALISM: 2.5s delay for card payments
    if (paymentMethod === "card") {
      setIsPayingCard(true);
      await new Promise(resolve => setTimeout(resolve, 2500));
      setIsPayingCard(false);
    }

    try {
      const orderData = {
        branch_id: selectedBranch !== "all" ? selectedBranch : null,
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          extras: item.extras || [],
          notes: item.notes || "",
          printed_quantity: item.printed_quantity || 0
        })),
        order_type: orderType,
        table_number: orderType === "local" ? finalTableNumber : null,
        address: orderType === "delivery" ? {
          street: deliveryAddress,
          city: "Almería",
          postal_code: "04001"
        } : null,
        customer_name: finalCustomerName || "Cliente Caja",
        customer_phone: customerPhone || "",
        payment_method: paymentMethod,
        notes: orderNotes,
        subtotal: subtotal,
        delivery_fee: deliveryFee,
        discount: discountAmount,
        discount_description: appliedDiscount ? `${appliedDiscount.percent}% - ${appliedDiscount.reason || 'Descuento'} (Auth: ${appliedDiscount.authorized_by})` : null,
        total: total,
        payment_status: paymentMethod === "cash" ? "paid" : "pending",
        source: "pos"
      };

      if (editingOrderId) {
        // ACTUALIZAR PEDIDO EXISTENTE
        await axios.put(`${API}/orders/${editingOrderId}`, {
          items: orderData.items,
          order_type: orderData.order_type,
          table_number: orderData.table_number,
          customer_name: orderData.customer_name,
          customer_phone: orderData.customer_phone,
          notes: orderData.notes,
          total: orderData.total
        }, { withCredentials: true });

        // Si se seleccionó pagar (EFECTIVO o TARJETA) y no es "pending", procesar el pago
        if (paymentMethod === "cash" || paymentMethod === "card") {
          try {
            await axios.put(`${API}/orders/${editingOrderId}/pay`, {
              payment_method: paymentMethod
            }, { withCredentials: true });
            toast.success(`Pago registrado: ${paymentMethod === 'cash' ? 'Efectivo' : 'Tarjeta'}`, { icon: "💰" });

            // Imprimir tras cobro - Desactivado (El PrintQueueWorker lo hará)
            /*
            if (originalOrder) {
              printService.printOrder({ ...originalOrder, payment_status: 'paid', payment_method: paymentMethod }, "Conexo POS");
            }
            */
          } catch (payError) {
            console.error("Error processing payment update:", payError);
            toast.error("Pedido actualizado pero falló el cobro. Intente cobrar nuevamente.");
          }
        }

        toast.success("Pedido actualizado con éxito", { icon: "✅" });
        setEditingOrderId(null);
        setOriginalOrder(null);
        clearCart();
        setShowPayment(false);
        fetchData();
      } else if (isOnline) {
        // CREAR NUEVO PEDIDO ONLINE
        try {
          const response = await axios.post(`${API}/orders/pos`, orderData, {
            withCredentials: true
          });

          const orderId = response.data.order_id;
          const fullOrder = response.data;

          if (sessionStorage.getItem("demo_mode") === "true") {
            toast.success("✨ ¡PEDIDO REALIZADO CON ÉXITO!", {
              description: `Modo Demo: El pedido #${orderId.slice(-6)} se ha registrado correctamente en el sistema.`,
              duration: 8000,
              icon: "🚀",
              style: {
                background: "#10B981",
                color: "#fff",
                border: "none"
              }
            });
          } else {
            toast.success(`Pedido #${orderId.slice(-6)} creado`);
            toast.success("Pedido enviado a cola de impresión");
          }

          // Se elimina impresión directa: el PrintQueueWorker se encargará de esto automáticamente
          // al detectar el nuevo pedido en estado 'pending'
          /*
          console.log("[AdminPOS] Iniciando secuencia de impresión...");
          printService.printOrder(fullOrder, "Conexo POS", null, "kitchen");
          if (paymentMethod === "cash" || paymentMethod === "card") {
            setTimeout(() => {
              printService.printOrder(fullOrder, "Conexo POS");
            }, 2000);
          }
          */

          // CRM: Save/Update Customer automatically
          if (customerPhone && customerPhone.length >= 9) {
            try {
              await axios.post(`${API}/admin/customers`, {
                phone: customerPhone,
                name: finalCustomerName,
                address: deliveryAddress
              }, { withCredentials: true });
            } catch (crmError) {
              console.error("Error saving customer to CRM:", crmError);
            }
          }

          clearCart();
          setShowPayment(false);
          setCashReceived("");
          fetchData(); // Refresh pending orders

          // DEMO REALISM: Simulate kitchen ready after 45s
          if (sessionStorage.getItem("demo_mode") === "true") {
            simulateOrderReady(orderId, orderData.table_number, orderData.customer_name);
          }

        } catch (apiError) {
          // Si hay error online, guardar offline como fallback
          console.error("API Error, guardando offline:", apiError);

          if (sessionStorage.getItem("demo_mode") === "true") {
            // En demo no queremos que guarde offline y muera ahí si es un 403 o similar
            const detail = apiError.response?.data?.detail || "";
            toast.error(`Error en Demo: ${detail || "La venta no se pudo procesar."}`, {
              description: "Verifica que no estés bloqueado o intenta recargar la página."
            });
          } else {
            await offlineDB.savePendingOrder(orderData, user?.company_id);
            toast.warning("⚠️ Sin conexión - Pedido guardado offline");
          }

          clearCart();
          setShowPayment(false);
          setCashReceived("");
        }
      } else {
        // Modo Offline: Guardar en IndexedDB
        await offlineDB.savePendingOrder(orderData, user?.company_id);
        toast.warning("📴 MODO OFFLINE - Pedido guardado (se sincronizará automáticamente)");

        clearCart();
        setShowPayment(false);
        setCashReceived("");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || "Error al crear el pedido";
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Open payment dialog for pending order
  const openPendingPaymentDialog = (order) => {
    setSelectedPendingOrder(order);
    setPendingPaymentMethod("cash");
    setCashReceived("");
    setShowPendingPayment(true);
  };

  // Process payment for pending order
  const handlePayPendingOrder = async () => {
    if (!selectedPendingOrder) return;

    setIsProcessing(true);
    try {
      await axios.put(`${API}/orders/${selectedPendingOrder.order_id}/pay`, {
        payment_method: pendingPaymentMethod
      }, {
        withCredentials: true
      });

      const methodLabel = pendingPaymentMethod === "cash" ? "Efectivo" : "Tarjeta";
      toast.success(`Pedido #${selectedPendingOrder.order_id.slice(-6)} cobrado (${methodLabel}) - ${selectedPendingOrder.total.toFixed(2)} €`);

      // Se elimina impresión directa: el PrintQueueWorker lo detectará
      // console.log("[AdminPOS] Imprimiendo Ticket de Cliente...");
      // printService.printOrder({ ...selectedPendingOrder, payment_status: 'paid', payment_method: pendingPaymentMethod }, "Conexo POS");

      setSelectedPendingOrder(null);
      setShowPendingPayment(false);
      setCashReceived("");
      fetchData(); // Refresh pending orders
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error("Error al procesar el pago");
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate change
  const change = cashReceived ? parseFloat(cashReceived) - total : 0;
  const pendingChange = selectedPendingOrder && cashReceived
    ? parseFloat(cashReceived) - (parseFloat(selectedPendingOrder?.total) || 0)
    : 0;

  if (isLoading) {
    return (
      <AdminLayout title="TPV / Caja">
        <div className="h-[calc(100vh-120px)] flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="TPV / Caja" noPadding noFooter>
      <div className="flex flex-col h-[calc(100vh-0px)] lg:h-screen bg-gray-900 text-white overflow-hidden p-0 m-0">
        {/* Render Invisible Worker */}
        {selectedBranch && selectedBranch !== 'all' && (
          <PrintQueueWorker
            branchId={selectedBranch}
            onStatusChange={setPrintQueueStatus}
          />
        )}

        {/* Header */}
        <header className="flex-none bg-gray-800 border-b border-gray-700 p-2 sm:p-4 flex items-center justify-between shadow-md z-10">
          <div className="flex items-center gap-3">
            <OnlineStatusIndicator />
            {/* Printer Status & Queue */}
            <div className="flex items-center gap-2">
              <Button
                variant={printQueueStatus.count > 0 ? "destructive" : "outline"}
                size="sm"
                className={`h-9 border-gray-600 gap-2 ${printQueueStatus.processing ? "animate-pulse" : ""}`}
                title="Cola de impresión"
                onClick={() => setShowPrintSettings(true)}
              >
                <Printer className={`w-4 h-4 ${printQueueStatus.count > 0 ? "text-white" : "text-gray-400"}`} />
                {printQueueStatus.count > 0 && (
                  <span className="font-bold">{printQueueStatus.count}</span>
                )}
              </Button>

              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium cursor-pointer transition-colors ${printerStatus === "connected"
                  ? "bg-green-500/10 border-green-500/50 text-green-500 hover:bg-green-500/20"
                  : "bg-blue-500/10 border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                  }`}
                onClick={() => setShowPrintSettings(true)}
              >
                <Printer className="w-3.5 h-3.5" />
                <span className="text-[10px] hidden sm:inline uppercase font-bold">
                  {printerStatus === 'connected' ? "TICKERA LOCAL OK" : "MODO AGENTE"}
                </span>
              </div>

              {/* Delivery Hub Status (New) - Link for better navigation */}
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-all cursor-pointer hover:scale-105 active:scale-95 ${hubStatus === "connected"
                  ? "bg-purple-500/10 border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                  : "bg-gray-800 border-gray-700 text-gray-500 opacity-50"
                  }`}
                onClick={() => navigate("/admin/delivery")}
              >
                <Truck className="w-3.5 h-3.5" />
                <div className="flex flex-col items-start leading-none">
                  <span className="text-[8px] font-black uppercase tracking-tighter">DELIVERY HUB</span>
                  <span className="text-[10px] font-bold">{ridersAvailable} RIDERS</span>
                </div>
              </div>
            </div>

            {editingOrderId && (
              <Badge className="bg-blue-600 animate-pulse ml-2">MODO EDICIÓN</Badge>
            )}
          </div>
        </header>

        {((user?.role === "admin" && (!user?.assigned_branches || user?.assigned_branches.length !== 1)) || (user?.assigned_branches?.length > 1)) && (
          <div className="w-[200px]">
            <Select value={selectedBranch || "all"} onValueChange={setSelectedBranch}>
              <SelectTrigger className="bg-gray-900 border-gray-800 text-white h-9">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <SelectValue placeholder="Sucursal" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800 text-white">
                <SelectItem value="all">Todas las sucursales</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* No Active Shift Warning */}
        {
          !checkingShift && !activeShift && (
            <div className="bg-red-900/30 border-2 border-red-500 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-red-400 font-bold text-lg">Sin turno activo</p>
                  <p className="text-red-300 text-sm">
                    Debes abrir un turno para {
                      (() => {
                        const bName = selectedBranch !== 'all' ? branches.find(b => b.branch_id === selectedBranch)?.name : null;
                        if (!bName) return 'la sucursal';
                        // Si el nombre de la sucursal es el default de Pizza Planet pero la empresa ya tiene otro nombre, usar el de la empresa
                        if (bName.toUpperCase().includes("PIZZA PLANET") && user?.company_name && !user.company_name.toUpperCase().includes("PIZZA PLANET")) {
                           return user.company_name;
                        }
                        return bName;
                      })()
                    } en Reporte de Caja antes de poder vender
                  </p>
                </div>
                <Button
                  onClick={() => navigate(`/admin/cash?branch_id=${selectedBranch}`)}
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Abrir Turno
                </Button>
              </div>
            </div>
          )
        }

        {/* Edit Mode Banner */}
        {
          editingOrderId && (
            <div className="bg-blue-600 border-2 border-blue-400 rounded-xl p-4 flex items-center justify-between shadow-lg animate-pulse-subtle">
              <div className="flex items-center gap-3">
                <div className="bg-white p-2 rounded-full">
                  <Edit2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-white font-black text-lg">MODO EDICIÓN ACTIVADO</p>
                  <p className="text-blue-100 text-sm font-medium">Actualizando Pedido #{editingOrderId.slice(-6)} - {originalOrder?.customer_name || `Mesa ${originalOrder?.table_number}`}</p>
                </div>
              </div>
              <Button
                onClick={cancelEditMode}
                variant="outline"
                className="bg-white text-blue-600 hover:bg-blue-50 border-none font-bold"
              >
                <X className="w-4 h-4 mr-2" />
                CANCELAR
              </Button>
            </div>
          )
        }

        <div className="flex gap-4 flex-1 overflow-hidden min-h-0">
          {/* Left Panel - Menu or Pending Orders */}
          <div className="flex-1 flex flex-col bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            {/* View Mode Toggle */}
            <div className="flex p-3 border-b border-gray-800 gap-2">
              <Button
                size="sm"
                onClick={() => setViewMode("new")}
                className={viewMode === "new"
                  ? "bg-primary text-black hover:bg-yellow-400 flex-1"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700 flex-1"}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Nuevo Pedido
              </Button>
              <Button
                size="sm"
                onClick={() => setViewMode("pending")}
                className={viewMode === "pending"
                  ? "bg-green-600 text-white hover:bg-green-500 flex-1"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700 flex-1"}
              >
                <Receipt className="w-4 h-4 mr-2" />
                Por Cobrar ({pendingOrders.length})
              </Button>
              <Button
                size="sm"
                onClick={() => setViewMode("history")}
                className={viewMode === "history"
                  ? "bg-blue-600 text-white hover:bg-blue-500 flex-1"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700 flex-1"}
              >
                <History className="w-4 h-4 mr-2" />
                Historial
              </Button>
            </div>

            {viewMode === "new" ? (
              <>
                {/* Categories */}
                <div className="flex gap-2 p-3 border-b border-gray-800 overflow-x-auto">
                    {categories.map(cat => (
                      <Button
                        key={cat.category_id}
                        size="sm"
                        variant={selectedCategory === cat.category_id ? "default" : "outline"}
                        onClick={() => setSelectedCategory(cat.category_id)}
                        className={selectedCategory === cat.category_id
                          ? "bg-primary text-black hover:bg-yellow-400 whitespace-nowrap"
                          : "border-gray-700 text-gray-300 hover:text-white whitespace-nowrap"}
                      >
                        {cat.icon} {cat.name}
                      </Button>
                    ))}
                  </div>

                  {/* View Mode Toggle: Images vs Boxes */}
                  <div className="flex justify-end px-3 py-1 gap-2">
                    <button
                      onClick={() => setShowImages(!showImages)}
                      className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-400 hover:text-primary transition-colors"
                    >
                      {showImages ? (
                        <><Eye className="w-3 h-3" /> MODO GALERÍA</>
                      ) : (
                        <><Boxes className="w-3 h-3" /> MODO BOTONES (TPV)</>
                      )}
                    </button>
                  </div>

                {/* Products Grid */}
                <div className="flex-1 p-3 overflow-y-auto">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {filteredItems.map(item => (
                      <button
                        key={item.item_id}
                        onClick={() => addToCart(item)}
                        className={`transition-all active:scale-95 text-left shadow-lg ${showImages
                          ? "bg-gray-800 hover:bg-gray-700 rounded-lg p-3 border border-gray-700 hover:border-primary"
                          : "bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700 hover:border-primary rounded-xl p-4 h-28 flex flex-col justify-between"
                          }`}
                      >
                        {showImages ? (
                          <>
                            <div className="aspect-square rounded-lg overflow-hidden mb-2 bg-gray-700">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                onError={(e) => e.target.src = "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=200"}
                              />
                            </div>
                            <h3 className="font-bold text-white text-xs truncate">{item.name}</h3>
                            <p className="text-primary font-bold">{parseFloat(item.price).toFixed(2)} €</p>
                          </>
                        ) : (
                          <>
                            <h3 className="font-black text-white text-sm leading-tight uppercase line-clamp-2 drop-shadow-md">
                              {item.name}
                            </h3>
                            <div className="flex justify-between items-end">
                              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter">PRECIO UN.</span>
                              <p className="text-primary font-black text-lg leading-none">
                                {parseFloat(item.price).toFixed(2)}€
                              </p>
                            </div>
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : viewMode === "pending" ? (
              /* Pending Orders View */
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Filters */}
                <div className="flex gap-2 p-3 border-b border-gray-800">
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("all")}
                    className={pendingFilter === "all"
                      ? "bg-primary text-black hover:bg-yellow-400"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
                  >
                    Todos ({baseForCounts.length})
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("local")}
                    className={pendingFilter === "local"
                      ? "bg-green-500 text-black hover:bg-green-400"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
                  >
                    🍽️ Local ({localCount})
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("delivery")}
                    className={pendingFilter === "delivery"
                      ? "bg-blue-500 text-white hover:bg-blue-400"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
                  >
                    🛵 Delivery ({deliveryCount})
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("pickup")}
                    className={pendingFilter === "pickup"
                      ? "bg-primary text-black hover:bg-yellow-400"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
                  >
                    🛍️ Pickup ({pickupCount})
                  </Button>

                  {/* INTEGRATIONS FILTER (New) */}
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("integrations")}
                    className={pendingFilter === "integrations"
                      ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/20 animate-pulse"
                      : "bg-gray-800 text-purple-400 hover:text-purple-300 hover:bg-gray-700"}
                  >
                    🔌 Integraciones ({integrationsCount})
                  </Button>
                </div>

                {/* Search Bar */}
                <div className="px-3 pb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Buscar por #pedido, nombre, mesa, teléfono..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white pl-10"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Orders List */}
                <div className="flex-1 p-3 overflow-y-auto">
                  {filteredPendingOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-gray-900/50 rounded-xl border border-dashed border-gray-800">
                      {searchQuery ? (
                        <>
                          <Search className="w-12 h-12 mb-4 text-gray-600" />
                          <p className="text-white font-bold text-lg">No se encontró el pedido</p>
                          <p className="text-gray-500 text-sm mb-4">No hay pedidos pendientes que coincidan con "{searchQuery}"</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSearchQuery("")}
                            className="border-gray-700 text-gray-400"
                          >
                            Ver todos los pedidos
                          </Button>
                        </>
                      ) : (
                        <>
                          <Check className="w-16 h-16 mb-4 text-green-500" />
                          <p className="text-xl font-bold text-white">¡Todo cobrado!</p>
                          <p className="text-sm text-gray-500">No hay pedidos pendientes de pago en este momento</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredPendingOrders.map(order => (
                        <div
                          key={order.order_id}
                          className={`bg-gray-800 rounded-lg p-4 border-2 transition-all cursor-pointer hover:border-green-500 ${selectedPendingOrder?.order_id === order.order_id
                            ? "border-green-500"
                            : "border-gray-700"
                            }`}
                          onClick={() => setSelectedPendingOrder(order)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-white">#{order.order_id.slice(-6)}</span>
                              <Badge className={
                                order.order_type === "local" ? "bg-green-500 text-black" :
                                  order.order_type === "pickup" ? "bg-primary text-black" :
                                    "bg-blue-500 text-white"
                              }>
                                {order.order_type === "local" ? "🍽️ LOCAL" :
                                  order.order_type === "pickup" ? "🛍️ RECOGER" :
                                    "🛵 DELIVERY"}
                              </Badge>
                              <Badge className={
                                order.status === "ready" ? "bg-primary text-black animate-pulse shadow-lg shadow-primary/20" :
                                  order.status === "delivered" ? "bg-green-500 text-black" :
                                    "bg-gray-600 truncate"
                              }>
                                {order.status === "ready" ? "🔔 LISTO" :
                                  order.status === "delivered" ? "✅ ENTREGADO" :
                                    order.status}
                              </Badge>
                              {order.is_addition && (
                                <Badge className="bg-orange-600 text-white animate-pulse">AGREGADO</Badge>
                              )}

                              {/* INTEGRATION SOURCE BADGE */}
                              {isPlatformOrder(order) && (
                                <Badge className={`font-black tracking-tighter ${(order.source || "").toLowerCase().includes("uber") ? "bg-[#06C167] text-white shadow-[#06C167]/40 shadow-sm" :
                                  (order.source || "").toLowerCase().includes("glovo") ? "bg-[#FFC244] text-black shadow-[#FFC244]/40 shadow-sm" :
                                    "bg-[#EA000B] text-white shadow-[#EA000B]/40 shadow-sm"
                                  }`}>
                                  {((order.source || "").toLowerCase().includes("uber") ? "UBER" :
                                    (order.source || "").toLowerCase().includes("glovo") ? "GLOVO" :
                                      "JUST EAT")}
                                </Badge>
                              )}
                            </div>
                            <span className="font-bold text-primary text-lg">{order.total.toFixed(2)} €</span>
                          </div>

                          <div className="text-sm text-gray-400 mb-2 flex flex-wrap gap-2">
                            {order.order_type === "local" && order.table_number && (
                              <span className="text-green-400 font-medium">
                                {!isNaN(order.table_number) ? `Mesa ${order.table_number}` : `Loc: ${order.table_number}`}
                              </span>
                            )}
                            {order.order_type === "pickup" && (
                              <span className="text-yellow-400 font-medium">Para recoger</span>
                            )}

                            {/* Mostrar nombre del cliente si existe, si no mostrar quién lo creó */}
                            {order.customer_name && order.customer_name !== "Cliente Caja" ? (
                              <span className="text-white font-bold">• {order.customer_name}</span>
                            ) : (
                              order.user_name && <span className="text-gray-500">• {order.user_name}</span>
                            )}

                            <span className="text-gray-600">•</span>
                            <span>{new Date(order.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</span>
                          </div>

                          <div className="text-sm text-gray-300">
                            {order.items.slice(0, 3).map((item, i) => (
                              <span key={i}>
                                {item.quantity}x {item.name}
                                {i < Math.min(order.items.length, 3) - 1 && ", "}
                              </span>
                            ))}
                            {order.items.length > 3 && <span className="text-gray-500"> +{order.items.length - 3} más</span>}

                            <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-700">
                              <button
                                type="button"
                                className="flex-1 h-8 px-2 text-xs border border-orange-500/50 text-orange-400 hover:bg-orange-500 hover:text-black rounded transition-colors flex items-center justify-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  axios.post(`${API}/orders/${order.order_id}/reprint/kitchen`, {}, { withCredentials: true })
                                    .then(() => toast.success("Comanda enviada"))
                                    .catch(() => toast.error("Error al imprimir"));
                                }}
                              >
                                <Printer className="w-3 h-3" /> COMANDA
                              </button>
                              <button
                                type="button"
                                className="flex-1 h-8 px-2 text-xs border border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white rounded transition-colors flex items-center justify-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  axios.post(`${API}/orders/${order.order_id}/reprint/customer`, {}, { withCredentials: true })
                                    .then(() => toast.success("Ticket enviado"))
                                    .catch(() => toast.error("Error al imprimir"));
                                }}
                              >
                                <Printer className="w-3 h-3" /> TICKET
                              </button>
                              <button
                                type="button"
                                className="flex-1 h-8 px-2 text-xs bg-blue-600 border border-blue-500 text-white hover:bg-blue-500 rounded transition-colors flex items-center justify-center gap-1 font-bold"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadOrderToEdit(order.order_id);
                                }}
                              >
                                <Plus className="w-3 h-3" /> AGREGAR
                              </button>
                              {order.order_type === "delivery" && !isPlatformOrder(order) && !order.dispatched_to_hub && (
                                <button
                                  type="button"
                                  className="flex-1 h-8 px-2 text-xs bg-purple-600 border border-purple-500 text-white hover:bg-purple-500 rounded transition-colors flex items-center justify-center gap-1 font-bold"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDispatchToHub(order);
                                  }}
                                >
                                  <Truck className="w-3 h-3" /> HUB
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* History View */
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Filters Top Bar */}
                <div className="p-3 border-b border-gray-800 space-y-3 bg-gray-900/50">
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    <Select
                      value={historyFilters.preset}
                      onValueChange={(v) => setHistoryFilters(prev => ({ ...prev, preset: v, offset: 0 }))}
                    >
                      <SelectTrigger className="w-[140px] bg-gray-800 border-gray-700 text-white h-8 text-xs">
                        <Calendar className="w-3 h-3 mr-2" />
                        <SelectValue placeholder="Periodo" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="today">Hoy</SelectItem>
                        <SelectItem value="yesterday">Ayer</SelectItem>
                        <SelectItem value="last7">Últimos 7 días</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={historyFilters.status}
                      onValueChange={(v) => setHistoryFilters(prev => ({ ...prev, status: v, offset: 0 }))}
                    >
                      <SelectTrigger className="w-[120px] bg-gray-800 border-gray-700 text-white h-8 text-xs">
                        <SelectValue placeholder="Estado" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="all">Cualquier Estado</SelectItem>
                        <SelectItem value="paid">Pagados (Cobrados)</SelectItem>
                        <SelectItem value="cancelled">Cancelados</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={historyFilters.type}
                      onValueChange={(v) => setHistoryFilters(prev => ({ ...prev, type: v, offset: 0 }))}
                    >
                      <SelectTrigger className="w-[120px] bg-gray-800 border-gray-700 text-white h-8 text-xs">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="all">Cualquier Tipo</SelectItem>
                        <SelectItem value="local">Local</SelectItem>
                        <SelectItem value="delivery">Delivery</SelectItem>
                        <SelectItem value="pickup">Pickup</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Buscar #pedido, cliente, mesa..."
                      value={historyFilters.q}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, q: e.target.value, offset: 0 }))}
                      className="bg-gray-800 border-gray-700 text-white pl-10 h-8 text-xs font-mono"
                    />
                  </div>
                </div>

                {/* Table / List */}
                <div className="flex-1 overflow-y-auto p-3">
                  {isLoadingHistory ? (
                    <div className="flex justify-center items-center h-full py-20">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : historyOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 py-10">
                      <History className="w-12 h-12 mb-4 opacity-20" />
                      <p className="font-bold">No se encontraron pedidos</p>
                      <p className="text-sm">Ajusta los filtros para ver más resultados</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {historyOrders.map(order => (
                        <div
                          key={order.order_id}
                          onClick={() => handleViewHistoryDetail(order)}
                          className="bg-gray-800/50 hover:bg-gray-800 border border-gray-800 hover:border-blue-500/50 rounded-lg p-3 transition-all cursor-pointer group"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-bold text-sm">#{order.order_id.slice(-6).toUpperCase()}</span>
                              <Badge className={`text-[10px] px-1 py-0 h-4 ${order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                order.status === 'cancelled' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                  'bg-primary/20 text-primary border-primary/30'
                                }`} variant="outline">
                                {order.payment_status === 'paid' ? 'PAGADO' : order.status.toUpperCase()}
                              </Badge>
                              <span className="text-gray-500 text-[10px]">
                                {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <span className="text-white font-bold text-sm tracking-tight">{(order.total || 0).toFixed(2)}€</span>
                          </div>
                          <div className="mt-1 flex justify-between items-end">
                            <div className="text-[10px] text-gray-400">
                              {order.order_type === 'local' ? `🪑 Mesa ${order.table_number}` :
                                order.order_type === 'delivery' ? `🛵 Delivery: ${order.customer_name}` :
                                  `🛍️ Pickup: ${order.customer_name}`}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Eye className="w-3 h-3 text-blue-400" />
                              <span className="text-[10px] text-blue-400 uppercase font-black">Detalles</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                <div className="p-2 border-t border-gray-800 flex items-center justify-between bg-gray-900/30">
                  <div className="text-[10px] text-gray-500">
                    Total: {historyTotal}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7 hover:bg-gray-800"
                      disabled={historyFilters.offset === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setHistoryFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
                      }}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center px-2 text-[10px] text-white font-mono">
                      {Math.floor(historyFilters.offset / historyFilters.limit) + 1} / {Math.ceil(historyTotal / historyFilters.limit) || 1}
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7 hover:bg-gray-800"
                      disabled={historyFilters.offset + historyFilters.limit >= historyTotal}
                      onClick={(e) => {
                        e.stopPropagation();
                        setHistoryFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }));
                      }}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Cart or Payment */}
          <div className="w-96 flex flex-col bg-gray-900 rounded-xl border border-gray-800 overflow-hidden h-full">
            {viewMode === "pending" && selectedPendingOrder ? (
              /* Payment Panel for Pending Order */
              <div className="flex flex-col h-full">
                <div className="p-3 border-b border-gray-800 bg-green-900/30">
                  <h3 className="font-bold text-white text-center">COBRAR PEDIDO</h3>
                  <p className="text-green-400 text-center text-sm">#{selectedPendingOrder.order_id.slice(-6)}</p>
                </div>
                <div className="flex-1 p-4 overflow-y-auto">
                  <div className="space-y-2 mb-4">
                    {selectedPendingOrder.items.map((item, i) => {
                      const itemPrice = parseFloat(item.price) || 0;
                      const itemQty = parseInt(item.quantity) || 1;
                      return (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-300">{itemQty}x {item.name}</span>
                          <span className="text-white">{(itemPrice * itemQty).toFixed(2)} €</span>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => loadOrderToEdit(selectedPendingOrder.order_id)}
                    className="w-full mb-4 border border-blue-500/30 text-blue-400 hover:bg-blue-500 hover:text-white gap-2 h-10 font-bold"
                  >
                    <Edit2 className="w-4 h-4" />
                    EDITAR / AGREGAR MÁS
                  </Button>
                  {selectedPendingOrder.order_type === "local" && selectedPendingOrder.table_number && (
                    <div className="bg-green-900/30 rounded-lg p-2 mb-4 text-center">
                      <span className="text-green-400 font-bold">Mesa {selectedPendingOrder.table_number}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-700 pt-4">
                    <div className="flex justify-between text-xl font-bold mb-4">
                      <span className="text-white">TOTAL</span>
                      <span className="text-primary">{(parseFloat(selectedPendingOrder.total) || 0).toFixed(2)} €</span>
                    </div>
                    {/* Payment Method Selection */}
                    <div className="space-y-3 mb-4">
                      <label className="text-sm text-gray-400">Método de pago</label>
                      <div className="flex gap-2">
                        <Button
                          data-testid="payment-method-cash"
                          onClick={() => setPendingPaymentMethod("cash")}
                          className={`flex-1 h-10 ${pendingPaymentMethod === "cash"
                            ? "bg-green-500 hover:bg-green-600 text-black"
                            : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                        >
                          <Banknote className="w-4 h-4 mr-2" />
                          EFECTIVO
                        </Button>
                        <Button
                          data-testid="payment-method-card"
                          onClick={() => setPendingPaymentMethod("card")}
                          className={`flex-1 h-10 ${pendingPaymentMethod === "card"
                            ? "bg-blue-500 hover:bg-blue-600 text-white"
                            : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          TARJETA
                        </Button>
                      </div>
                    </div>
                    {/* Cash Calculator - only show for cash */}
                    {pendingPaymentMethod === "cash" && (
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-400">Efectivo recibido</label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={cashReceived}
                            onChange={(e) => setCashReceived(e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white text-right text-lg"
                          />
                        </div>
                        {/* Quick amounts */}
                        <div className="grid grid-cols-4 gap-2">
                          {[5, 10, 20, 50].map(amount => (
                            <Button
                              key={amount}
                              variant="outline"
                              size="sm"
                              className="border-gray-700 text-gray-300 hover:bg-gray-800"
                              onClick={() => setCashReceived(amount.toString())}
                            >
                              {amount}€
                            </Button>
                          ))}
                        </div>
                        {cashReceived && pendingChange >= 0 && (
                          <div className="flex justify-between items-center bg-green-900/50 rounded-lg p-3">
                            <span className="text-green-400">Cambio</span>
                            <span className="text-green-400 font-bold text-xl">{pendingChange.toFixed(2)} €</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Card payment info */}
                    {pendingPaymentMethod === "card" && (
                      <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 text-center">
                        <CreditCard className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                        <p className="text-blue-400">Procesa el pago en el datáfono</p>
                        <p className="text-blue-400/70 text-sm">y confirma cuando esté completado</p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Actions: Print Pre-check + Pay */}
                <div className="p-4 border-t border-gray-800 space-y-2">
                  <button
                    type="button"
                    className="w-full h-10 border border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white rounded-md flex items-center justify-center font-medium transition-colors"
                    onClick={() => {
                      if (selectedPendingOrder) {
                        toast.info("Imprimiendo pre-cuenta...");
                        // Force print as "Customer Ticket" without changing status
                        printService.printOrder(selectedPendingOrder, "Conexo POS");
                      }
                    }}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    IMPRIMIR PRE-CUENTA
                  </button>
                  <button
                    type="button"
                    className={`w-full text-lg h-14 rounded-md font-medium flex items-center justify-center ${pendingPaymentMethod === "cash"
                      ? "bg-green-600 hover:bg-green-500 text-white"
                      : "bg-blue-600 hover:bg-blue-500 text-white"
                      } ${isProcessing || (pendingPaymentMethod === "cash" && cashReceived && parseFloat(cashReceived) < (parseFloat(selectedPendingOrder.total) || 0)) ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isProcessing) {
                        handlePayPendingOrder();
                      }
                    }}
                    disabled={isProcessing || (pendingPaymentMethod === "cash" && cashReceived && parseFloat(cashReceived) < (parseFloat(selectedPendingOrder.total) || 0))}
                  >
                    {isProcessing ? (
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <>
                        {pendingPaymentMethod === "cash" ? <Banknote className="w-5 h-5 mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
                        COBRAR {(parseFloat(selectedPendingOrder.total) || 0).toFixed(2)} €
                      </>
                    )}
                  </button>

                  {/* DELIVERY HUB DISPATCH (New) */}
                  {selectedPendingOrder.order_type === "delivery" && (
                    <button
                      type="button"
                      className="w-full h-12 bg-purple-600 hover:bg-purple-500 text-white rounded-md flex items-center justify-center font-black transition-all shadow-lg shadow-purple-500/20 border-b-4 border-purple-800 active:border-b-0"
                      onClick={() => handleDispatchToHub(selectedPendingOrder)}
                      disabled={isProcessing}
                    >
                      <Truck className="w-4 h-4 mr-2 mb-1" />
                      DESPACHAR AL DELIVERY HUB
                    </button>
                  )}
                  <button
                    type="button"
                    className="w-full h-10 rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedPendingOrder(null);
                      setCashReceived("");
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              /* Normal Cart Panel */
              <>
                {/* Demo Banner Mini */}
                <div className="mx-3 mt-3">
                  <DemoBanner variant="mini" />
                </div>

                {/* Demo Tips Banner */}
                {sessionStorage.getItem("demo_mode") === "true" && (
                  <div className="mx-3 mt-3 mb-1 p-3 bg-purple-900/40 border border-purple-500/30 rounded-xl animate-in slide-in-from-top duration-500">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="p-1 bg-purple-500 rounded-md">
                        <Lock className="w-3.5 h-3.5 text-black" />
                      </div>
                      <span className="text-[11px] font-black uppercase tracking-wider text-purple-300">Tips de Prueba</span>
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-[10px] text-gray-300 leading-tight">
                        🎟️ Cupón 20% OFF: <span className="text-primary font-bold">DEMO20</span>
                      </p>
                      <p className="text-[10px] text-gray-300 leading-tight">
                        🔐 Pin Admin: <span className="text-primary font-bold">1234</span> (usa para descuentos)
                      </p>
                      <p className="text-[10px] text-purple-200 mt-1 font-bold">
                        💡 Tip: Deja la mesa vacía y el sistema asignará la primera libre.
                      </p>
                      <p className="text-[11px] text-purple-200/80 italic font-medium">
                        "Haz una venta y mira cómo sube el Reporte de Caja"
                      </p>
                    </div>

                    {/* SIMULATE INTEGRATIONS */}
                    <div className="mt-3 pt-3 border-t border-purple-500/30">
                      <p className="text-[9px] font-black text-purple-300 uppercase mb-2 tracking-tighter">Simular Pedido Externo</p>
                      <div className="grid grid-cols-3 gap-2">
                        <button onClick={() => simulateIntegrationOrder('uber')} className="flex flex-col items-center justify-center p-1 bg-black border border-gray-700 rounded-lg hover:border-[#06C167] transition-all group">
                          <span className="text-[8px] font-black text-[#06C167]">UBER</span>
                        </button>
                        <button onClick={() => simulateIntegrationOrder('glovo')} className="flex flex-col items-center justify-center p-1 bg-[#FFC244] border border-[#FFC244]/50 rounded-lg hover:border-black transition-all group">
                          <span className="text-[8px] font-black text-black">GLOVO</span>
                        </button>
                        <button onClick={() => simulateIntegrationOrder('justeat')} className="flex flex-col items-center justify-center p-1 bg-[#EA000B] border border-[#EA000B]/50 rounded-lg hover:border-white transition-all group">
                          <span className="text-[8px] font-black text-white">JUST EAT</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Order Type Toggle */}
                <div className="p-3 border-b border-gray-800">
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      className={`h-9 px-3 py-2 rounded-md font-medium inline-flex items-center justify-center text-xs ${orderType === "local"
                        ? "bg-green-500 hover:bg-green-600 text-black"
                        : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                      onClick={() => setOrderType("local")}
                    >
                      <UtensilsCrossed className="w-4 h-4 mr-1" />
                      LOCAL
                    </button>
                    <button
                      type="button"
                      className={`h-9 px-3 py-2 rounded-md font-medium inline-flex items-center justify-center text-xs ${orderType === "pickup"
                        ? "bg-primary hover:bg-yellow-600 text-black"
                        : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                      onClick={() => {
                        setOrderType("pickup");
                        setTableNumber("");
                      }}
                    >
                      <ShoppingBag className="w-4 h-4 mr-1" />
                      RECOGER
                    </button>
                    <button
                      type="button"
                      className={`h-9 px-3 py-2 rounded-md font-medium inline-flex items-center justify-center text-xs ${orderType === "delivery"
                        ? "bg-blue-500 hover:bg-blue-600 text-white"
                        : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                      onClick={() => {
                        setOrderType("delivery");
                        setTableNumber("");
                      }}
                    >
                      <Truck className="w-4 h-4 mr-1" />
                      DELIVERY
                    </button>
                  </div>
                  {/* Order Details */}
                  <div className="mt-3 space-y-2">
                    {orderType === "local" ? (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Nº Mesa"
                          value={tableNumber}
                          onChange={(e) => setTableNumber(e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white text-center text-lg font-bold w-24"
                          type="number"
                          inputMode="numeric"
                        />
                        <Input
                          placeholder="Nombre Cliente (Opcional)"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white flex-1"
                        />
                      </div>
                    ) : orderType === "pickup" ? (
                      <div className="space-y-2 p-2 bg-primary/5 rounded-lg border border-primary/10">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input
                              placeholder="Teléfono"
                              value={customerPhone}
                              onChange={(e) => setCustomerPhone(e.target.value)}
                              className={`bg-gray-800 border-gray-700 text-white ${isFrequentCustomer ? "border-green-500" : ""}`}
                            />
                            {isSearchingCustomer ? (
                              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
                              </div>
                            ) : (
                              <button
                                onClick={handleManualCustomerSearch}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
                              >
                                <Search className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {customerPhone && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-10 w-8 text-gray-500 hover:text-red-400"
                              onClick={() => {
                                setCustomerPhone("");
                                setCustomerName("");
                                setIsFrequentCustomer(false);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <Input
                          placeholder="Nombre del cliente"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          className={`bg-gray-800 border-gray-700 text-white text-center font-bold ${isFrequentCustomer ? "border-green-500" : ""}`}
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 p-2 bg-blue-500/5 rounded-lg border border-blue-500/10">
                        {customerDataConfirmed && customerName ? (
                          /* COMPACT SUMMARY VIEW */
                          <div className="flex items-center justify-between p-2 bg-blue-900/20 rounded border border-blue-500/20">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white text-sm">{customerName}</span>
                                {isFrequentCustomer && <span className="text-[10px] bg-green-500 text-black px-1 rounded font-bold">VIP</span>}
                              </div>
                              <div className="text-xs text-gray-400">{deliveryAddress}</div>
                              {customerPhone && <div className="text-xs text-gray-500">{customerPhone}</div>}
                            </div>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setCustomerDataConfirmed(false)}>
                              <Edit className="w-4 h-4 text-blue-400" />
                            </Button>
                          </div>
                        ) : (
                          /* FULL INPUT FORM */
                          <>
                            <div className="flex gap-2">
                              <div className="relative flex-1">
                                <Input
                                  placeholder="Teléfono (Buscar)"
                                  value={customerPhone}
                                  onChange={(e) => setCustomerPhone(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleManualCustomerSearch();
                                      if (customerName) setCustomerDataConfirmed(true);
                                    }
                                  }}
                                  className={`bg-gray-800 border-gray-700 text-white ${isFrequentCustomer ? "border-green-500 border-2" : ""}`}
                                />
                                {isSearchingCustomer ? (
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
                                  </div>
                                ) : (
                                  <button
                                    onClick={handleManualCustomerSearch}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-400"
                                  >
                                    <Search className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              {(customerPhone || customerName || deliveryAddress) && (
                                <div className="flex">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-10 w-8 text-gray-500 hover:text-red-400"
                                    onClick={() => {
                                      setCustomerPhone("");
                                      setCustomerName("");
                                      setDeliveryAddress("");
                                      setIsFrequentCustomer(false);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                  {/* Manual collapse button if data is present */}
                                  {(isFrequentCustomer || (customerName && deliveryAddress)) && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-10 w-8 text-blue-500 hover:text-blue-400 bg-blue-500/10 ml-1"
                                      onClick={() => setCustomerDataConfirmed(true)}
                                      title="Ocultar detalles"
                                    >
                                      <ChevronUp className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                            <Input
                              placeholder="Nombre"
                              value={customerName}
                              onChange={(e) => setCustomerName(e.target.value)}
                              className={`flex-1 bg-gray-800 border-gray-700 text-white ${isFrequentCustomer ? "border-green-500/50" : ""}`}
                            />
                            <Input
                              placeholder="Dirección de entrega"
                              value={deliveryAddress}
                              onChange={(e) => setDeliveryAddress(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && customerName && deliveryAddress) {
                                  setCustomerDataConfirmed(true);
                                }
                              }}
                              className={`bg-gray-800 border-gray-700 text-white ${isFrequentCustomer ? "border-green-500/50" : ""}`}
                            />
                            {/* Manual Confirm Button for new users */}
                            {customerName && deliveryAddress && !isFrequentCustomer && (
                              <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-500 text-xs h-7" onClick={() => setCustomerDataConfirmed(true)}>
                                Confirmar Datos
                              </Button>
                            )}
                          </>
                        )}

                        {/* Payment Note Always Visible but Compact */}
                        <div className="flex gap-2 mt-1 pt-1 border-t border-gray-700/50">
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">PAGA CON:</span>
                            <Input
                              placeholder="50"
                              value={paymentNote}
                              type="number"
                              onChange={(e) => setPaymentNote(e.target.value)}
                              className="bg-gray-800 border-gray-700 text-white pl-20"
                            />
                          </div>
                          <div className="flex-1 bg-gray-800 border border-gray-700 rounded-md flex items-center justify-center">
                            {paymentNote && !isNaN(paymentNote) && parseFloat(paymentNote) >= total ? (
                              <span className="text-primary font-bold">
                                CAMBIO: {(parseFloat(paymentNote) - total).toFixed(2)} €
                              </span>
                            ) : (
                              <span className="text-gray-500 text-xs">Sin cambio</span>
                            )}
                          </div>
                        </div>

                        {isFrequentCustomer && (
                          <div className="text-[10px] text-green-500 font-bold uppercase tracking-widest text-center animate-pulse mt-2">
                            ✨ Cliente Frecuente Detectado
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Cart Items - Scrollable Area - Asegurando mínimo de altura para ver 2-3 items */}
                <div className="flex-1 overflow-y-auto p-2 min-h-[100px] scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent scrollbar-w-[4px]">
                  {cart.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-500">
                      <ShoppingCart className="w-12 h-12 mb-2" />
                      <p>Carrito vacío</p>
                      <p className="text-sm">Selecciona productos del menú</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {cart.map(item => {
                        const itemPrice = parseFloat(item.price) || 0;
                        const itemQty = parseInt(item.quantity) || 0;
                        const isOld = editingOrderId && (item.printed_quantity > 0);
                        const isNewAdded = editingOrderId && (itemQty > (item.printed_quantity || 0));

                        return (
                          <div key={item.item_id} className={`bg-gray-800 rounded-lg p-2 flex items-center gap-2 transition-colors ${isNewAdded ? 'border-l-4 border-blue-500 bg-blue-900/10' : ''}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h4 className="font-medium text-white text-xs">{item.name}</h4>
                                {isOld && !isNewAdded && <span className="text-[9px] px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded uppercase font-bold">Ya en cocina</span>}
                                {isNewAdded && <span className="text-[9px] px-1.5 py-0.5 bg-blue-600 text-white rounded uppercase font-bold animate-pulse">Nuevo / Extra</span>}
                              </div>
                              <p className="text-primary text-xs">{(itemPrice * itemQty).toFixed(2)} €</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                                onClick={() => updateQuantity(item.item_id, -1)}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="text-white font-bold w-5 text-center text-sm">{itemQty}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                                onClick={() => updateQuantity(item.item_id, 1)}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>

                              {!(isOld) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-400 hover:bg-red-500/10 ml-0.5"
                                  onClick={() => removeFromCart(item.item_id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Notes */}
                <div className="px-2 pb-1">
                  <Input
                    placeholder="Notas del pedido..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white text-xs h-8"
                  />
                </div>
                {/* Totals */}
                <div className="px-3 py-2 border-t border-gray-800 space-y-1">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal</span>
                    <span>{subtotal.toFixed(2)} €</span>
                  </div>
                  {orderType === "delivery" && (
                    <div className="flex justify-between text-gray-400">
                      <span>Envío</span>
                      <span>{deliveryFee.toFixed(2)} €</span>
                    </div>
                  )}
                  {/* Discount Section */}
                  {appliedDiscount ? (
                    <div className="flex justify-between text-green-400 items-center">
                      <div className="flex items-center gap-1">
                        <Percent className="w-3 h-3" />
                        <span>Descuento ({appliedDiscount.percent}%)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>-{discountAmount.toFixed(2)} €</span>
                        <button
                          type="button"
                          onClick={() => setAppliedDiscount(null)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowDiscountDialog(true)}
                      disabled={cart.length === 0}
                      className="w-full flex items-center justify-center gap-2 text-sm text-purple-400 hover:text-purple-300 py-1 disabled:opacity-50"
                    >
                      <Percent className="w-4 h-4" />
                      Aplicar descuento
                    </button>
                  )}
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-green-500 font-bold">
                      <span className="flex items-center gap-1">
                        <Percent className="w-3 h-3" /> Descuento
                        {appliedCouponInfo && <span className="text-[10px] bg-green-500/10 px-1 rounded">({appliedCouponInfo.code})</span>}
                      </span>
                      <span>-{discountAmount.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-black text-white pt-1 border-t border-gray-800">
                    <span>TOTAL</span>
                    <span className="text-primary">{total.toFixed(2)} €</span>
                  </div>
                </div>

                {/* POS Quick Actions (Discount/Coupons) */}
                <div className="grid grid-cols-2 gap-2 px-3 pb-2 mt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-[10px] font-bold border-gray-700 text-gray-400 hover:text-white"
                    onClick={() => setShowDiscountDialog(true)}
                  >
                    DESC. ADMIN
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 text-[10px] font-bold border-gray-700 ${appliedCouponInfo ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'text-gray-400'} hover:text-white`}
                    onClick={() => setShowCouponDialog(true)}
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {appliedCouponInfo ? 'CUPÓN OK' : 'USAR CUPÓN'}
                  </Button>
                </div>
                {/* Action Buttons */}
                {/* Action Buttons - Compact Layout */}
                <div className="px-3 py-2 border-t border-gray-800 space-y-2">
                  {/* Row 1: Payment Methods */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="flex-1 h-9 px-2 bg-green-500 hover:bg-green-600 text-black font-bold rounded-md inline-flex items-center justify-center text-sm disabled:opacity-50 disabled:pointer-events-none"
                      onClick={() => {
                        setPaymentMethod("cash");
                        setShowPayment(true);
                      }}
                      disabled={(cart.length === 0 && !editingOrderId) || isProcessing}
                    >
                      <Banknote className="w-4 h-4 mr-1.5" />
                      EFECTIVO
                    </button>
                    <button
                      type="button"
                      className="flex-1 h-9 px-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-md inline-flex items-center justify-center text-sm disabled:opacity-50 disabled:pointer-events-none"
                      onClick={() => {
                        setPaymentMethod("card");
                        setShowPayment(true);
                      }}
                      disabled={(cart.length === 0 && !editingOrderId) || isProcessing}
                    >
                      <CreditCard className="w-4 h-4 mr-1.5" />
                      TARJETA
                    </button>
                  </div>

                  {/* Row 2: Cancel (Small) + Confirm (Main) */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="w-[30%] h-10 px-0 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold rounded-md inline-flex items-center justify-center transition-colors disabled:opacity-50 disabled:pointer-events-none"
                      onClick={clearCart}
                      disabled={cart.length === 0 || editingOrderId}
                      title={editingOrderId ? "No se puede cancelar en modo edición" : "Cancelar Pedido"}
                    >
                      <X className="w-5 h-5" />
                    </button>

                    <button
                      type="button"
                      className={`flex-1 h-10 px-2 text-black font-bold rounded-md inline-flex items-center justify-center text-sm disabled:opacity-50 disabled:pointer-events-none ${editingOrderId ? "bg-blue-500 hover:bg-blue-600 text-white" : "bg-orange-500 hover:bg-orange-600"}`}
                      onClick={handleConfirmOrderWithoutPayment}
                      disabled={cart.length === 0 || isProcessing}
                      data-testid="confirm-delayed-order-btn"
                    >
                      {editingOrderId ? "GUARDAR" :
                        orderType === "local" ? "COCINA" :
                          orderType === "pickup" ? "CONFIRMAR" : "CONFIRMAR"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Payment Dialog */}
        <Dialog open={showPayment} onOpenChange={setShowPayment}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-primary flex items-center gap-2">
                {paymentMethod === "cash" ? (
                  <><Banknote className="w-5 h-5" /> Pago en Efectivo</>
                ) : (
                  <><CreditCard className="w-5 h-5" /> Pago con Tarjeta</>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Order Summary */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Tipo:</span>
                  <Badge className={
                    orderType === "local" ? "bg-green-500 text-black" :
                      orderType === "pickup" ? "bg-primary text-black" :
                        "bg-blue-500"
                  }>
                    {orderType === "local" ? `🍽️ Mesa ${tableNumber}` :
                      orderType === "pickup" ? `🛍️ ${customerName}` :
                        "🛵 Delivery"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Items:</span>
                  <span className="text-white">{cart.reduce((sum, i) => sum + i.quantity, 0)} productos</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold pt-2 border-t border-gray-700">
                  <span className="text-white">TOTAL:</span>
                  <span className="text-primary">{total.toFixed(2)} €</span>
                </div>
              </div>

              {/* Cash Calculator */}
              {paymentMethod === "cash" && (
                <div className="space-y-3">
                  <label className="text-sm text-gray-400">Efectivo recibido:</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white text-2xl text-center font-bold h-14"
                    autoFocus
                  />

                  {/* Quick amounts */}
                  <div className="grid grid-cols-4 gap-2">
                    {[5, 10, 20, 50].map(amount => (
                      <Button
                        key={amount}
                        variant="outline"
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                        onClick={() => setCashReceived(amount.toString())}
                      >
                        {amount}€
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
                    onClick={() => setCashReceived(total.toFixed(2))}
                  >
                    Importe exacto ({total.toFixed(2)} €)
                  </Button>

                  {/* Change */}
                  {parseFloat(cashReceived) >= total && (
                    <div className="bg-green-900/50 border border-green-700 rounded-lg p-4 text-center">
                      <p className="text-green-400 text-sm">CAMBIO A DEVOLVER</p>
                      <p className="text-3xl font-bold text-green-400">{change.toFixed(2)} €</p>
                    </div>
                  )}
                </div>
              )}

              {paymentMethod === "card" && (
                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 text-center">
                  <CreditCard className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                  <p className="text-blue-400">Procesa el pago en el datáfono</p>
                  <p className="text-blue-400/70 text-sm">y confirma cuando esté completado</p>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowPayment(false);
                  setCashReceived("");
                }}
                className="h-9 px-4 py-2 border border-gray-700 text-gray-300 hover:bg-gray-800 rounded-md font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleProcessOrder}
                disabled={isProcessing || (paymentMethod === "cash" && parseFloat(cashReceived) < total)}
                className="h-9 px-4 py-2 bg-green-500 hover:bg-green-600 text-black font-bold rounded-md inline-flex items-center justify-center disabled:opacity-50 disabled:pointer-events-none"
              >
                {isProcessing ? (
                  "Procesando..."
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    CONFIRMAR PEDIDO
                  </>
                )}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Discount Dialog */}
        <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-purple-400 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Aplicar Descuento (Requiere Admin)
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-gray-400 block mb-2">Porcentaje de descuento</label>
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    placeholder="10"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">Motivo del descuento</label>
                <Input
                  placeholder="Ej: Cliente frecuente, promoción..."
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div className="border-t border-gray-700 pt-4">
                <label className="text-sm text-gray-400 block mb-2">
                  🔐 Contraseña del Administrador
                </label>
                <Input
                  type="password"
                  placeholder="Introduce la contraseña del admin"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              {discountPercent && subtotal > 0 && (
                <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Subtotal:</span>
                    <span className="text-white">{subtotal.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-purple-400">Descuento ({discountPercent}%):</span>
                    <span className="text-purple-400">-{((subtotal * parseFloat(discountPercent || 0)) / 100).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-purple-700 pt-2 mt-2">
                    <span className="text-white">Nuevo total:</span>
                    <span className="text-primary">{(subtotal - (subtotal * parseFloat(discountPercent || 0)) / 100 + deliveryFee).toFixed(2)} €</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowDiscountDialog(false);
                  setAdminPassword("");
                  setDiscountPercent("");
                  setDiscountReason("");
                }}
                className="h-9 px-4 py-2 border border-gray-700 text-gray-300 hover:bg-gray-800 rounded-md font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleVerifyDiscount}
                disabled={isVerifyingDiscount || !discountPercent || !adminPassword}
                className="h-9 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-md inline-flex items-center justify-center disabled:opacity-50 disabled:pointer-events-none"
              >
                {isVerifyingDiscount ? (
                  <span className="animate-spin mr-2">⏳</span>
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                AUTORIZAR DESCUENTO
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Coupon Dialog */}
        <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-primary flex items-center gap-2 font-black uppercase tracking-tight">
                <Tag className="w-5 h-5" />
                Aplicar Cupón de Marketing
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-1.5">Código del Cupón</label>
                <Input
                  placeholder="EJ: CUMPLE15"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="bg-gray-800 border-gray-700 text-white text-center font-black tracking-widest h-12 text-lg"
                  autoFocus
                />
              </div>

              {appliedCouponInfo && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-center gap-3">
                  <div className="bg-green-500/20 p-2 rounded-lg">
                    <Check className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-green-500 uppercase">Cupón Activo: {appliedCouponInfo.code}</p>
                    <p className="text-[10px] text-gray-400">Ahorro: {appliedCouponInfo.discount_type === 'percent' ? appliedCouponInfo.discount_value + '%' : '€' + appliedCouponInfo.discount_value}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Button
                className="w-full bg-primary hover:bg-yellow-400 text-black font-black"
                onClick={handleValidateCoupon}
                disabled={isValidatingCoupon || !couponCode}
              >
                {isValidatingCoupon ? "VALIDANDO..." : "APLICAR BENEFICIO"}
              </Button>
              <Button
                variant="ghost"
                className="text-gray-500 hover:text-white"
                onClick={() => setShowCouponDialog(false)}
              >
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* History Detail Dialog with Timeline */}
        <Dialog open={showHistoryDetail} onOpenChange={setShowHistoryDetail}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
            <DialogHeader className="p-4 border-b border-gray-800 flex flex-row items-center justify-between">
              <div>
                <DialogTitle className="text-blue-400 text-xl font-black">
                  DETALLES DEL PEDIDO #{selectedHistoryOrder?.order_id.slice(-6).toUpperCase()}
                </DialogTitle>
                <div className="flex gap-2 mt-1">
                  <Badge variant="outline" className="border-gray-700 text-gray-400 text-[10px]">
                    {new Date(selectedHistoryOrder?.created_at).toLocaleString()}
                  </Badge>
                  <Badge className={selectedHistoryOrder?.payment_status === 'paid' ? 'bg-green-500 text-black' : 'bg-primary text-black'}>
                    {selectedHistoryOrder?.payment_status === 'paid' ? 'COBRADO' : 'PENDIENTE'}
                  </Badge>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowHistoryDetail(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto p-4 flex gap-6">
              {/* Info Column */}
              <div className="flex-1 space-y-4">
                <section className="bg-gray-800/50 rounded-lg p-4 border border-gray-800">
                  <h4 className="text-xs font-black text-gray-500 uppercase mb-3 tracking-widest">Resumen de Productos</h4>
                  <div className="space-y-2">
                    {selectedHistoryOrder?.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <div className="flex gap-2">
                          <span className="text-primary font-bold">{item.quantity}x</span>
                          <span className="text-gray-200">{item.name}</span>
                        </div>
                        <span className="text-white font-mono">{(item.price * item.quantity).toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-700 flex justify-between font-black text-lg">
                    <span className="text-gray-400">TOTAL</span>
                    <span className="text-primary">{(selectedHistoryOrder?.total || 0).toFixed(2)}€</span>
                  </div>
                </section>

                <section className="bg-gray-800/50 rounded-lg p-4 border border-gray-800">
                  <h4 className="text-xs font-black text-gray-500 uppercase mb-3 tracking-widest">Estado de Impresión</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[10px] text-gray-500 uppercase font-bold">Ticket Cliente</p>
                      <div className="flex items-center gap-2">
                        <Badge className={selectedHistoryOrder?.customer_print_status === 'printed' ? 'bg-green-600' : 'bg-yellow-600'}>
                          {selectedHistoryOrder?.customer_print_status === 'printed' ? 'Impreso' : 'Pendiente/Error'}
                        </Badge>
                        <Button size="icon" variant="outline" className="h-7 w-7 border-gray-700" onClick={() => handleReprint('customer')}>
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-[10px] text-gray-500 uppercase font-bold">Comanda Cocina</p>
                      <div className="flex items-center gap-2">
                        <Badge className={selectedHistoryOrder?.kitchen_print_status === 'printed' ? 'bg-green-600' : 'bg-yellow-600'}>
                          {selectedHistoryOrder?.kitchen_print_status === 'printed' ? 'Impreso' : 'Pendiente'}
                        </Badge>
                        <Button size="icon" variant="outline" className="h-7 w-7 border-gray-700" onClick={() => handleReprint('kitchen')}>
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              {/* Audit Timeline Column */}
              <div className="w-80 bg-black/20 rounded-lg p-4 border border-gray-800 flex flex-col">
                <h4 className="text-xs font-black text-gray-500 uppercase mb-4 tracking-widest flex items-center gap-2">
                  <Clock className="w-3 h-3" /> Auditoría / Historial
                </h4>
                <div className="flex-1 space-y-6 relative border-l border-gray-800 ml-2 pl-6 overflow-y-auto pr-2">
                  {selectedHistoryOrder?.events?.map((event, idx) => (
                    <div key={idx} className="relative">
                      {/* Dot */}
                      <div className={`absolute -left-[31px] w-2.5 h-2.5 rounded-full border-2 border-gray-900 ${event.type.includes('ok') ? 'bg-green-500' :
                        event.type.includes('failed') ? 'bg-red-500' :
                          event.type.includes('created') ? 'bg-blue-500' :
                            'bg-primary'
                        }`} />

                      <p className="text-[10px] text-gray-500 font-mono">
                        {new Date(event.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </p>
                      <p className="text-xs font-bold text-gray-200 mt-0.5">
                        {event.type.replace(/_/g, ' ').toUpperCase()}
                      </p>
                      {event.actor_role && (
                        <p className="text-[9px] text-blue-400 font-black uppercase mt-0.5">
                          Actor: {event.actor_role}
                        </p>
                      )}
                      {event.meta && Object.keys(event.meta).length > 0 && (
                        <div className="mt-1 bg-white/5 rounded p-1.5 text-[10px] text-gray-400 font-mono">
                          {JSON.stringify(event.meta)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className="p-4 bg-gray-800/50 border-t border-gray-800">
              {selectedHistoryOrder?.order_type === 'delivery' && !isPlatformOrder(selectedHistoryOrder) && !selectedHistoryOrder?.dispatched_to_hub && (
                <Button
                  className="bg-purple-600 hover:bg-purple-500 text-white font-black"
                  onClick={() => {
                    handleDispatchToHub(selectedHistoryOrder);
                    setShowHistoryDetail(false);
                  }}
                >
                  <Truck className="w-4 h-4 mr-2" />
                  DESPACHAR AL HUB
                </Button>
              )}
              <Button variant="outline" className="border-gray-700 text-gray-300" onClick={() => setShowHistoryDetail(false)}>
                Cerrar Detalle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


      {/* Printer Settings Dialog */}
      <Dialog open={showPrintSettings} onOpenChange={setShowPrintSettings}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-primary flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Configuración de Impresora
            </DialogTitle>
          </DialogHeader>
          <PrinterSettings />
        </DialogContent>
      </Dialog>
      {/* Card Payment Overlay (Premium Realism) */}
      <Dialog open={isPayingCard} onOpenChange={() => { }}>
        <DialogContent className="bg-gray-900/95 border-blue-500/50 text-white max-w-xs p-8 text-center">
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
              <CreditCard className="w-8 h-8 text-blue-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
            <div>
              <h3 className="text-xl font-black text-blue-400 mb-1">DATÁFONO ACTIVO</h3>
              <p className="text-sm text-gray-400">Procesando transacción...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </AdminLayout >
  );
}
